import type { FeatureFlagValue } from './endpoints';
import { useFeatureFlagMap } from './featureFlagsContext';
import type { FlagKey } from './flags';

/**
 * Read one feature flag for the current user (PTV2-1067 D28).
 *
 * Typed against the `flags.ts` registry — an unregistered key is a compile
 * error (and with the registry empty, the hook is intentionally uncallable:
 * the dormant state). Absent key = `false` for booleans / the supplied
 * `defaultValue` for variants; the FE does NO permission logic itself — a
 * `visibleWhen`-gated flag the user may not see is simply absent from the map.
 *
 * @param key the registered flag key (e.g. `feature.darkMode`)
 * @param defaultValue returned when the flag is absent/null (default `false`)
 * @returns the flag value — `boolean` for BOOL flags, the variant token for ENUM
 *
 * @example
 *   useFeature('feature.darkMode')             // -> boolean
 *   useFeature('feature.worklist', 'control')  // -> variant, defaulted when absent
 */
export function useFeature(key: FlagKey, defaultValue: boolean | string = false): boolean | string {
  const flags = useFeatureFlagMap();
  const value: FeatureFlagValue | undefined = flags[key];
  return value ?? defaultValue;
}
